import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';
import { Settings, DollarSign, Zap, CreditCard, Eye, EyeOff } from 'lucide-react';

interface PlatformSettings {
  commission: {
    rate: number;
    taxHoldPercentage: number;
  };
  express: {
    slaTimeoutMinutes: number;
    maxQueueSize: number;
  };
  ai: {
    rateLimitPerMinute: number;
    maxTokensPerRequest: number;
  };
  payments: {
    minWithdrawalAmount: number;
    withdrawalProcessingDays: number;
  };
  features: {
    darkModeEnabled: boolean;
    expressEnabled: boolean;
    aiEnabled: boolean;
  };
}

type TabType = 'commission' | 'express' | 'ai' | 'payments' | 'features';

export function AdminSettingsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('commission');

  const { data: settings, isLoading } = useQuery<PlatformSettings>({
    queryKey: ['settings'],
    queryFn: () => fetch('/api/v1/settings').then((res) => res.json()),
  });

  const updateSettingMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      const res = await fetch(`/api/v1/settings/${key}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ value }),
      });
      if (!res.ok) throw new Error('Failed to update setting');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  const handleToggle = (key: string, currentValue: boolean) => {
    updateSettingMutation.mutate({ key, value: !currentValue });
  };

  const handleUpdate = (key: string, value: number) => {
    updateSettingMutation.mutate({ key, value });
  };

  const tabs = [
    { id: 'commission' as TabType, label: 'Comissão', icon: DollarSign },
    { id: 'express' as TabType, label: 'Expressa', icon: Zap },
    { id: 'ai' as TabType, label: 'IA', icon: Settings },
    { id: 'payments' as TabType, label: 'Pagamentos', icon: CreditCard },
    { id: 'features' as TabType, label: 'Funcionalidades', icon: Eye },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Configurações da Plataforma</h1>
      </div>

      <div className="flex gap-2 border-b">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'commission' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Configurações de Comissão
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SettingItem
                label="Taxa de Comissão"
                description="Percentual que a plataforma recebe sobre cada transação"
                value={`${((settings?.commission.rate || 0) * 100).toFixed(0)}%`}
              >
                <input
                  type="range"
                  min="0"
                  max="50"
                  step="1"
                  defaultValue={(settings?.commission.rate || 0.2) * 100}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    document.getElementById('commission-rate-display')!.textContent = `${value}%`;
                  }}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value) / 100;
                    handleUpdate('platform.commission_rate', value);
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span id="commission-rate-display" className="text-lg font-medium">
                  {((settings?.commission.rate || 0.2) * 100).toFixed(0)}%
                </span>
              </SettingItem>

              <SettingItem
                label="Taxa de Imposto Retido"
                description="Percentual retido para impostos"
                value={`${((settings?.commission.taxHoldPercentage || 0) * 100).toFixed(0)}%`}
              >
                <input
                  type="range"
                  min="0"
                  max="20"
                  step="1"
                  defaultValue={(settings?.commission.taxHoldPercentage || 0.1) * 100}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    document.getElementById('tax-hold-display')!.textContent = `${value}%`;
                  }}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value) / 100;
                    handleUpdate('platform.tax_hold_percentage', value);
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span id="tax-hold-display" className="text-lg font-medium">
                  {((settings?.commission.taxHoldPercentage || 0.1) * 100).toFixed(0)}%
                </span>
              </SettingItem>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'express' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Configurações Expressa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SettingItem
                label="Timeout SLA (minutos)"
                description="Tempo máximo que um paciente aguarda na fila expressa"
                value={`${settings?.express.slaTimeoutMinutes || 15} minutos`}
              >
                <input
                  type="number"
                  min="5"
                  max="60"
                  defaultValue={settings?.express.slaTimeoutMinutes || 15}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 5 && value <= 60) {
                      handleUpdate('express.sla_timeout_minutes', value);
                    }
                  }}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </SettingItem>

              <SettingItem
                label="Tamanho Máximo da Fila"
                description="Número máximo de pacientes na fila expressa"
                value={settings?.express.maxQueueSize || 100}
              >
                <input
                  type="number"
                  min="10"
                  max="500"
                  defaultValue={settings?.express.maxQueueSize || 100}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 10 && value <= 500) {
                      handleUpdate('express.max_queue_size', value);
                    }
                  }}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </SettingItem>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'ai' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Configurações de IA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SettingItem
                label="Limite de Requisições por Minuto"
                description="Número máximo de requisições à IA por minuto"
                value={`${settings?.ai.rateLimitPerMinute || 30} req/min`}
              >
                <input
                  type="number"
                  min="1"
                  max="100"
                  defaultValue={settings?.ai.rateLimitPerMinute || 30}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 1 && value <= 100) {
                      handleUpdate('ai.rate_limit_per_minute', value);
                    }
                  }}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </SettingItem>

              <SettingItem
                label="Máximo de Tokens por Requisição"
                description="Limite de tokens para cada requisição à IA"
                value={settings?.ai.maxTokensPerRequest || 4000}
              >
                <input
                  type="number"
                  min="1000"
                  max="32000"
                  step="1000"
                  defaultValue={settings?.ai.maxTokensPerRequest || 4000}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 1000 && value <= 32000) {
                      handleUpdate('ai.max_tokens_per_request', value);
                    }
                  }}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </SettingItem>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Configurações de Pagamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SettingItem
                label="Valor Mínimo de Saque"
                description="Valor mínimo para solicitação de saque"
                value={`R$ ${(settings?.payments.minWithdrawalAmount || 50).toFixed(2)}`}
              >
                <input
                  type="number"
                  min="10"
                  max="1000"
                  step="5"
                  defaultValue={settings?.payments.minWithdrawalAmount || 50}
                  onBlur={(e) => {
                    const value = parseFloat(e.target.value);
                    if (value >= 10 && value <= 1000) {
                      handleUpdate('payment.min_withdrawal_amount', value);
                    }
                  }}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </SettingItem>

              <SettingItem
                label="Dias para Processamento"
                description="Dias úteis para processar saques"
                value={`${settings?.payments.withdrawalProcessingDays || 3} dias`}
              >
                <input
                  type="number"
                  min="1"
                  max="10"
                  defaultValue={settings?.payments.withdrawalProcessingDays || 3}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 1 && value <= 10) {
                      handleUpdate('payment.withdrawal_processing_days', value);
                    }
                  }}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </SettingItem>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'features' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Funcionalidades
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <ToggleItem
                label="Modo Escuro"
                description="Ativa o tema escuro na plataforma"
                enabled={settings?.features.darkModeEnabled || false}
                onToggle={() =>
                  handleToggle('feature.dark_mode_enabled', settings?.features.darkModeEnabled || false)
                }
              />

              <ToggleItem
                label="Consulta Expressa"
                description="Ativa o sistema de filas expressas"
                enabled={settings?.features.expressEnabled || true}
                onToggle={() =>
                  handleToggle('feature.express_enabled', settings?.features.expressEnabled || true)
                }
              />

              <ToggleItem
                label="Funcionalidades de IA"
                description="Ativa recursos de inteligência artificial"
                enabled={settings?.features.aiEnabled || true}
                onToggle={() =>
                  handleToggle('feature.ai_enabled', settings?.features.aiEnabled || true)
                }
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

interface SettingItemProps {
  label: string;
  description: string;
  value: string | number;
  children: React.ReactNode;
}

function SettingItem({ label, description, value: _value, children }: SettingItemProps) {
  return (
    <div className="flex items-start justify-between py-3 border-b last:border-b-0">
      <div className="flex-1">
        <h4 className="font-medium text-gray-900">{label}</h4>
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      </div>
      <div className="flex items-center gap-3">{children}</div>
    </div>
  );
}

interface ToggleItemProps {
  label: string;
  description: string;
  enabled: boolean;
  onToggle: () => void;
}

function ToggleItem({ label, description, enabled, onToggle }: ToggleItemProps) {
  return (
    <div className="flex items-start justify-between py-3 border-b last:border-b-0">
      <div className="flex-1">
        <h4 className="font-medium text-gray-900">{label}</h4>
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      </div>
      <button
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled ? 'bg-blue-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
        {enabled ? (
          <EyeOff className="absolute right-0.5 h-3 w-3 text-white" />
        ) : (
          <Eye className="absolute right-0.5 h-3 w-3 text-gray-500" />
        )}
      </button>
    </div>
  );
}
